
import { configurationAlreadyExistsKey } from 'app/area/indicator-factors/constants';
import { codePickersId } from 'app/area/indicator-factors/item/code-pickers/types';
import {
  CodeActionType, ReadOnlyType, InitializeCodeItemPayload, ReadOnlyConfigurationValue,
  ReadOnlyConfiguration, CodeItemAccessConfiguration, ClearAllValidation
} from 'app/area/indicator-factors/item/types';
import { AccessCodes, CodeFieldWireKey, CodetableAccess } from 'app/area/indicator-factors/types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

export const handleCreateCodeItem = (payload: InitializeCodeItemPayload): void => {
  shouldCreateNewCodeItem(payload) && payload.createCodeItem();
  shouldBlockAccess(payload) && payload.codetableBlockAccess(payload?.breadcrumbs);
};

export const handleLoadCodeItem = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload): void => {
  const { codetableId, item: { fetchingCodeId }, loadCodeItem, clearValidation } = payload;

  const accessCodes = payload && previousPayload && accessCodesLoaded(payload, previousPayload);
  const payloadChanged = payload && previousPayload && (isDefinedIncludingZero(codetableId) && fetchingCodeId === undefined
    && hasPayloadChanged(payload, previousPayload));

  if (accessCodes || payloadChanged) {
    clearValidation();
    loadCodeItem(payload);
  }
};

export const handleLoadCodetableOptions = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload): void => {
  const {
    item: {
      codeItem: {
        substanceCategory: currSubstanceCategory,
        indicatorSubstanceCategory: currIndicatorSubstanceCategory,
        substance: currSubstanceValue,
        indicatorSubstance: currIndicatorSubstanceValue
      } = {}
    } = {},
    loadCodetableOptions
  } = payload;

  const {
    item: {
      codeItem: {
        substanceCategory: prevSubstanceCategory,
        indicatorSubstance: prevIndicatorSubstanceValue
      } = {}
    } = {}
  } = previousPayload;

  if (currIndicatorSubstanceValue !== prevIndicatorSubstanceValue) {
    loadCodetableOptions(
      CodeFieldWireKey.uomTypeIndicator,
      codePickersId.substance,
      {
        categoryId: currIndicatorSubstanceCategory?.value,
        siblingId: currIndicatorSubstanceValue?.value,
        code: { text: currIndicatorSubstanceValue?.value }
      },
      {
        limitFieldsTo: [],
        includeInactiveCodes: { enabled: false }
      }
    );
  }

  if (payload.actionType === CodeActionType.Edit && !!currSubstanceCategory && !prevSubstanceCategory) {
    loadCodetableOptions(
      CodeFieldWireKey.substance,
      codePickersId.substance,
      {
        categoryId: currSubstanceCategory?.value,
        siblingId: currSubstanceValue?.value
      },
      {
        limitFieldsTo: [CodeFieldWireKey.code, CodeFieldWireKey.defaultUomType],
        includeInactiveCodes: { enabled: false }
      }
    );
  }
};

const shouldBlockAccess = (payload: InitializeCodeItemPayload) => {
  return (payload.actionType === CodeActionType.Add || payload.actionType === CodeActionType.Duplicate)
    && (!payload?.access?.data || !payload?.access?.data?.codetables?.create);
};

const shouldCreateNewCodeItem = (payload: InitializeCodeItemPayload) => {
  const { codeId } = payload;
  return !codeId && codeId !== 0;
};

const hasPayloadChanged = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload) => {
  const { codetableId, codeId, parentId, actionType } = payload;
  return codetableId !== previousPayload.codetableId || codeId !== previousPayload.codeId
    || parentId !== previousPayload.parentId || actionType !== previousPayload.actionType;
};

const accessCodesLoaded = (payload: InitializeCodeItemPayload, previousPayload: InitializeCodeItemPayload) => {
  const { access: { data: { codes: currAccessCodes } = {} } = {} } = payload;
  const { access: { data: { codes: prevAccessCodes } = {} } = {} } = previousPayload;

  return !prevAccessCodes && !!currAccessCodes;
};

export const canLoadCodesAccess = ({ canCreate, administrationCentral }) => (!canCreate && !administrationCentral) || administrationCentral;

export const readOnlyConfiguration = (value: ReadOnlyConfigurationValue): ReadOnlyConfiguration => {

  if (!value) {
    return undefined;
  }

  const { actionType, codeId, administrationCentral, canCreate, canEdit } = value;
  const isNotSelected = codeId === 0;
  const edit = administrationCentral || canEdit;

  const notSelectedWithoutAdminCentral = isNotSelected && !administrationCentral;
  const notSelectedWithoutMetaDescription = isNotSelected && edit;
  const notSelectedWithMetaDescription = isNotSelected && canEdit && canCreate && !administrationCentral;

  return {
    [CodeActionType.Add]: {
      [ReadOnlyType.save]: !canCreate,
      [ReadOnlyType.all]: !canCreate
    },
    [CodeActionType.Duplicate]: {
      [ReadOnlyType.save]: !canCreate,
      [ReadOnlyType.all]: !canCreate
    },
    [CodeActionType.Edit]: {
      [ReadOnlyType.save]: !notSelectedWithMetaDescription
        && (!edit || notSelectedWithoutAdminCentral || notSelectedWithoutMetaDescription),
      [ReadOnlyType.all]: !edit || isNotSelected
    }
  }[actionType];
};

export const codeAccessConfiguration = (
  codetableId: number,
  access: CodetableAccess,
  listItemAccess: AccessCodes
): CodeItemAccessConfiguration => {

  const canCreate = !!(access && access.codetables && access.codetables.create);
  const canDelete = !!(access && access.codes && access.codes.delete);
  const canDeleteOnDemand = !!(listItemAccess && listItemAccess.delete);
  const canEditOnDemand = !!listItemAccess?.edit;
  const isCentralAdmin = !!(access && access.codetables && access.codetables.administrationCentral);

  return {
    canCreate,
    canDuplicate: canCreate,
    canDelete: !!(canDelete || canDeleteOnDemand || (canCreate && isCentralAdmin)),
    canEdit: !!(canEditOnDemand || isCentralAdmin),
    isCentralAdmin
  };
};

export const clearAllValidation = (value: ClearAllValidation) =>
  !!value.validation?.[configurationAlreadyExistsKey] && !!value.validation?.[value.codetableKey] && value.clearValidation();
